import React, { useState, useEffect } from 'react';
import _ from 'lodash';
import { closeModal } from 'actions/common';
import { ProtocolIdSelector } from 'selectors';
import { connect, useSelector } from 'react-redux';
import { withAppContext } from '@ctsu/loader';
import { bindActionCreators } from 'redux';
import atoms from 'ctsutheme/components/atoms';
import { FormViewContent } from 'components/FormViewContent';
import { saveRelations } from 'actions/protocols/opensetup';
import { openSnackbar } from 'helpers/Notifier';
import { httpStatusEnum } from 'appconstants';
import { openConfirmation } from 'helpers/Confirmation';
import { bindLogger } from 'helpers/BindLogger';
import { ReactFormSelect, ValidatorForm } from 'components/FormHelpers/FormInputs';
import { useCtxParseValue, GetFromCtxValue } from 'helpers';
import { compose, withHandlers, withState } from 'recompose';
import { CTSUCardActions } from 'components';
import { withChangeHandlers } from 'helpers/Change';
import {
  GroupProtocolNumber, Step, Required, Days, Start, End, Comments
} from './index';

const { Divider } = atoms;

export const StudyNumber = ({ state, protocolList, setState }) => {
  const [suggestions, setSuggestions] = useState([]);
  const [studyNum, selStudyNum] = useState(null);

  useEffect(() => {
    if (!_.isEmpty(protocolList)) {
      setSuggestions(protocolList);
    } else {
      setSuggestions([]);
    }
  }, [setSuggestions, protocolList]);

  const handleInputChange = val => {
    const lcVal = val && val.toLowerCase();
    setSuggestions(_.filter(protocolList,
      (item) => item.label.toLowerCase().includes(lcVal)));
  };

  useEffect(() => {
    if (state) {
      selStudyNum(_.find(protocolList, (item) => state.relatedProtocolNumber === item.value));
    } else {
      selStudyNum({});
    }
  }, [selStudyNum, protocolList, state]);

  return (
    <ReactFormSelect
      title="Related Protocol Number"
      name="relatedProtocolNumber"
      validators={['required']}
      errorMessages={['Required Field']}
      options={suggestions && suggestions.slice(0, 20)}
      value={studyNum || ''}
      onInputChange={handleInputChange}
      onChange={event => {
        selStudyNum(event);
        setState({ ...state, relatedProtocolNumber: event ? event.value : '' });
      }}
    />
  );
};

const studyTypeOptions = [{
  label: 'EMBEDDED',
  value: 'EMBEDDED'
}, {
  label: 'STAND-ALONE',
  value: 'STAND_ALONE'
}];

export const StudyType = ({ state, setState }) => {
  const [suggestions, setSuggestions] = useState(studyTypeOptions);
  const [relType, selRelType] = useState(null);

  const handleInputChange = val => {
    if (val) {
      setSuggestions(_.filter(suggestions,
        (item) => item.label.toLowerCase().includes(val.toLowerCase())));
    } else {
      setSuggestions(studyTypeOptions);
    }
  };

  useEffect(() => {
    if (state) {
      selRelType(_.find(suggestions, (item) => state.relationType === item.value));
    } else {
      selRelType({});
    }
  }, [selRelType, suggestions, state]);

  return (
    <ReactFormSelect
      title="Relation Type"
      name="relationType"
      validators={['required']}
      errorMessages={['Required Field']}
      options={suggestions}
      value={relType || ''}
      onInputChange={handleInputChange}
      onChange={event => {
        selRelType(event);
        setState({ ...state, relationType: event ? event.value : '' });
      }}
    />
  );
};

const EditProtocolRelationsUI = (props) => {
  const {
    cancel,
    state,
    reset,
    data,
    handleChange,
    handleDateChange,
    setState,
    saveProtocolRelations,
    steps,
    setSteps
  } = props;

  useEffect(() => {
    setState(data);
  }, [data, setState]);

  const { protocolList } = useCtxParseValue('LOV_PROTOCOL_LIST');
  const { protocolLogistics } = useSelector(s => s.openData.openData);

  useEffect(() => {
    setSteps(
      _.map(protocolLogistics, ({ step, prlId, description }) => ({
        label: `Step : ${step} (${description})`,
        value: step,
        prlId
      }))
    );
  }, [protocolLogistics, setSteps]);

  return (
    <ValidatorForm onSubmit={saveProtocolRelations} className="key-value-pair-wrapper">
      <FormViewContent
        primaryText="Study Number"
        secondaryText={(state && state.newRel) ? (
          <StudyNumber
            setState={setState}
            state={state}
            protocolList={protocolList}
          />
        ) : state.relatedProtocolNumber}
      />
      <Divider />
      <FormViewContent
        primaryText="Group Protocol Number"
        secondaryText={(
          <GroupProtocolNumber state={state} handleChange={handleChange} />
        )}
      />
      <Divider />
      <FormViewContent
        primaryText="Steps"
        secondaryText={(
          <Step state={state} setState={setState} steps={steps} />
        )}
      />
      <Divider />
      <FormViewContent
        primaryText="Study Type"
        secondaryText={(
          <StudyType state={state} setState={setState} />
        )}
      />
      <Divider />
      <FormViewContent
        primaryText="Required?"
        secondaryText={(
          <Required state={state} setState={setState} />
        )}
      />
      <Divider />
      <FormViewContent
        primaryText="Days"
        secondaryText={(
          <Days state={state} handleChange={handleChange} />
        )}
      />
      <Divider />
      <FormViewContent
        primaryText="Start Date"
        secondaryText={(
          <Start state={state} handleDateChange={handleDateChange} setState={setState} />
        )}
      />
      <Divider />
      <FormViewContent
        primaryText="End Date"
        secondaryText={(
          <End state={state} handleDateChange={handleDateChange} setState={setState} />
        )}
      />
      <Divider />
      <FormViewContent
        primaryText="Comments"
        secondaryText={(
          <Comments state={state} handleChange={handleChange} />
        )}
      />
      <Divider />
      <CTSUCardActions
        clearClick={reset}
        cancelClick={cancel}
        hideCancel='show'
        disabled={_.isEqual(state, data)}
      />
    </ValidatorForm>
  );
};

const withConnectState = connect(
  (state, ownProps) => ({
    protocolId: ProtocolIdSelector(state)
  }),
  dispatch => bindActionCreators(
    {
      onSaveRelations: saveRelations,
      onCloseModal: closeModal
    },
    dispatch
  )
);


const EditProtocolRelations = compose(
  withState('state', 'setState', {}),
  withState('steps', 'setSteps', []),
  withConnectState,
  withAppContext,
  withChangeHandlers,
  withHandlers({
    cancel: props => () => {
      const { onCloseModal } = props;
      onCloseModal();
    }
  }),
  withHandlers({
    saveProtocolRelations: props => postBody => {
      const {
        onSaveRelations, context, protocolId, cancel, state
      } = props;
      delete state.newRel;
      const confirmSave = () => {
        const postObj = {
          ...state,
          prtId: protocolId
        };
        bindLogger(onSaveRelations, context)(postObj, protocolId).subscribe(resp => {
          if (resp.status === httpStatusEnum.SUCCESS) {
            openSnackbar({
              message: GetFromCtxValue(context, 'PROTOCOL_RELATIONS_SUCCESS'),
              variant: 'success',
              name: 'relations'
            });
            cancel();
          }
        });
      };
      openConfirmation({
        message: GetFromCtxValue(context, 'PROTOCOL_RELATIONS_CONFIRM'),
        onAction: confirmSave,
        name: 'relations'
      });
    }
  })
)(EditProtocolRelationsUI);

export default EditProtocolRelations;
